// Direct-to-S3 image upload via api-v2 presign.
//
// Replaces the legacy multipart POST to utils/upload-image. Two-step:
//   1. POST /api/uploads/presign on api-v2 -> {url, key, public_url}.
//      axios interceptor (Services/Interceptors/Interceptors.jsx) adds the
//      authorization header automatically; api-v2's LegacyUserTokenAuthentication
//      bridge accepts the legacy staff token.
//   2. PUT the file bytes directly to the signed URL via vanilla fetch.
//      Plain fetch — NOT axios — so no interceptor headers; S3 rejects PUTs
//      that include unrelated headers in the signed request.
//
// Returns the same shape the legacy multipart helpers returned, so the 13
// upload-image consumers don't need any changes:
//   { status: 200, data: { data: [{ url, ... }] } }
//
// File-size cap (10 MB by default) is enforced client-side for fast feedback
// and sent to api-v2 so the server can enforce/sign the exact Content-Length.

import axios from "axios";
import { API_V2_URL } from "./apiUrl";

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB; matches the implicit cap on the legacy multipart route.

export class UploadTooLargeError extends Error {
  constructor(maxBytes) {
    super(`Image is larger than ${Math.round(maxBytes / 1024 / 1024)} MB. Please compress before uploading.`);
    this.name = "UploadTooLargeError";
    this.maxBytes = maxBytes;
  }
}

export async function uploadViaPresign(file, { mediaType = "Image", maxBytes = DEFAULT_MAX_BYTES } = {}) {
  if (!file) throw new Error("uploadViaPresign: file is required");
  if (file.size > maxBytes) throw new UploadTooLargeError(maxBytes);
  if (!API_V2_URL) {
    throw new Error("uploadViaPresign: REACT_APP_API_V2_URL is not set");
  }

  const presignBody = {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    media_type: mediaType,
    file_size: file.size,
  };

  const presignResp = await axios.post(`${API_V2_URL}/api/uploads/presign`, presignBody, {
    headers: { "Content-Type": "application/json" },
  });
  const { url: signedUrl, public_url } = presignResp.data || {};
  if (!signedUrl || !public_url) {
    throw new Error("uploadViaPresign: presign response missing url/public_url");
  }

  const putResp = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": presignBody.content_type,
      // boto3 signs ACL=public-read as x-amz-acl header on the presigned URL,
      // so the client must echo it or S3 returns SignatureDoesNotMatch.
      "x-amz-acl": "public-read",
    },
    body: file,
  });
  if (!putResp.ok) {
    throw new Error(`uploadViaPresign: S3 PUT failed (${putResp.status})`);
  }

  // Mirror legacy multipart response shape so the 13 consumers (which read
  // response.data.data[0].url) keep working without changes.
  return {
    status: 200,
    data: { data: [{ url: public_url }] },
  };
}

// Convenience: pull a File out of FormData and call uploadViaPresign.
// The legacy helpers (api/util.js#uploadImage, api/utils.js#uploadImage,
// Services/Utils/Utils.jsx#uploadImage) all accept FormData with an "image"
// field. They delegate here so the call sites don't change.
export async function uploadImageFromFormData(formData) {
  try {
    const file = formData.get("image");
    if (!file) throw new Error("uploadImageFromFormData: 'image' field missing from FormData");
    return await uploadViaPresign(file);
  } catch (error) {
    const responseData = error?.response?.data;
    const validationMessage = responseData && typeof responseData === "object"
      ? Object.values(responseData).flat().find((value) => typeof value === "string")
      : undefined;
    const message = responseData?.message
      || responseData?.detail
      || validationMessage
      || error?.message
      || "Image upload failed. Please try again.";

    // Preserve the legacy helper's response-based error contract. Existing
    // callers branch on response.status and can clear their loading state and
    // show this message instead of receiving an unhandled rejected promise.
    return {
      status: error?.response?.status || 500,
      data: { message },
    };
  }
}
